import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import "../../Styles/Admin/LeaveTable.css";
import { API_BASE_URL } from "../../apiConfig";

const LeaveTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [leaves, setLeaves] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsPerPage] = useState(10);


  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/leaves`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("idToken")}`,
          },
        });
        const data = await response.json();
        if (data.success) {
          setLeaves(data.leaves);
          setFilteredRequests(data.leaves);
        } else {
          console.error("Failed to fetch leave requests:", data.message);
        }
      } catch (err) {
        console.error("Error fetching leave requests:", err);
      }
    };

    fetchLeaveRequests();
  }, []);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = leaves.filter(
      (request) =>
        (request.name && request.name.toLowerCase().includes(term)) ||
        (request.reason && request.reason.toLowerCase().includes(term)) ||
        (request.status && request.status.toLowerCase().includes(term))
    );
    setFilteredRequests(filtered);
    setCurrentPage(1); 
  };


  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);
  
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  

  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);


  const handleApprove = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/leaves/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify({ status: "approved" }),
      });
      const data = await response.json();
      if (data.success) {
        
        setLeaves((prevLeaves) =>
          prevLeaves.map((request) =>
            request.id === id ? { ...request, status: "approved" } : request
          )
        );
        setFilteredRequests((prevRequests) =>
          prevRequests.map((request) =>
            request.id === id ? { ...request, status: "approved" } : request
          )
        );
      } else {
        console.error("Failed to approve leave request:", data.message);
      }
    } catch (err) {
      console.error("Error approving leave request:", err);
    }
  };

  const handleReject = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/leaves/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify({ status: "rejected" }),
      });
      const data = await response.json();
      if (data.success) {
        
        setLeaves((prevLeaves) =>
          prevLeaves.map((request) =>
            request.id === id ? { ...request, status: "rejected" } : request
          )
        );
        setFilteredRequests((prevRequests) =>
          prevRequests.map((request) =>
            request.id === id ? { ...request, status: "rejected" } : request
          )
        );
      } else {
        console.error("Failed to reject leave request:", data.message);
      }
    } catch (err) {
      console.error("Error rejecting leave request:", err);
    }
  };

  return (
    <motion.div
      className="leave-table-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <div className="leave-table-header">
        <h2 className="leave-table-title">Leave Requests</h2>
        <div className="leave-table-search-container">
          <input
            type="text"
            placeholder="Search leave requests..."
            className="leave-table-search-input"
            value={searchTerm}
            onChange={handleSearch}
          />
          <Search className="leave-table-search-icon" size={18} />
        </div>
      </div>

      <div className="leave-table-wrapper">
        <table className="leave-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentRequests.map((request) => (
              <motion.tr
                key={request.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <td>{request.name}</td>
                <td>{request.typeName || "Unknown"}</td>
                <td>
                  <span
                    className={`leave-status ${
                      request.status === "approved"
                        ? "status-approved"
                        : request.status === "pending"
                        ? "status-pending"
                        : "status-rejected"
                    }`}
                  >
                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </span>
                </td>
                <td>
                  {new Date(request.startDate._seconds * 1000).toLocaleDateString()} -{" "}
                  {new Date(request.endDate._seconds * 1000).toLocaleDateString()}
                </td>
                <td className="leave-actions">
                  {request.status?.toLowerCase().trim() === "pending" ? (
                    <>
                      <button
                        className="btn-approve"
                        onClick={() => handleApprove(request.id)}
                      >
                        Approve
                      </button>
                      <button
                        className="btn-reject"
                        onClick={() => handleReject(request.id)}
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <span className="action-done">Done</span>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default LeaveTable;
